import { useCallback, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { useQueryState } from 'nuqs'

import {
  selectIncident,
  selectIncidentDrawerNavigationContext,
  setIncident,
} from '@/redux/ui/uiSlice'
import type { IncidentDrawerNavigationIncident } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

type InputMethod = 'click' | 'keyboard'
type NavigationDirection = 'previous' | 'next'

const EDITABLE_TAGS = new Set(['input', 'textarea', 'select'])

type UseIncidentDrawerNavigationProps = {
  isOpen: boolean
}

export type UseIncidentDrawerNavigationResult = {
  isFromIncidentsTable: boolean
  showNavigation: boolean
  canNavigatePrevious: boolean
  canNavigateNext: boolean
  navigateToPreviousIncident: (inputMethod: InputMethod) => void
  navigateToNextIncident: (inputMethod: InputMethod) => void
}

export const useIncidentDrawerNavigation = ({
  isOpen,
}: UseIncidentDrawerNavigationProps): UseIncidentDrawerNavigationResult => {
  const dispatch = useDispatch()
  const [, setDisplayId] = useQueryState('displayId')
  const incidentId = useSelector(selectIncident)?.id
  const navigationContext = useSelector(selectIncidentDrawerNavigationContext)

  const isFromIncidentsTable = navigationContext?.isFromIncidentsTable ?? false
  const navigationIncidents = navigationContext?.incidents ?? []
  const hasNavigationContext = navigationIncidents.length > 0
  const currentIndex = navigationIncidents.findIndex(
    (incident) => incident.id === incidentId
  )
  // If the current incident is not in the refreshed table list, start navigation
  // from before index 0 so the next action lands on the first row.
  const effectiveIndex = currentIndex === -1 ? -1 : currentIndex

  const previousIncident: IncidentDrawerNavigationIncident | null =
    effectiveIndex > 0 ? navigationIncidents[effectiveIndex - 1] : null

  const nextIncident: IncidentDrawerNavigationIncident | null =
    effectiveIndex < navigationIncidents.length - 1
      ? navigationIncidents[effectiveIndex + 1]
      : null

  const canNavigatePrevious = hasNavigationContext && previousIncident !== null
  const canNavigateNext = hasNavigationContext && nextIncident !== null
  const shouldShowNavigation = isFromIncidentsTable

  const navigateToIncident = useCallback(
    (
      targetIncident: IncidentDrawerNavigationIncident | null,
      direction: NavigationDirection,
      inputMethod: InputMethod
    ) => {
      if (!targetIncident) {
        return
      }

      dispatch(setIncident({ id: targetIncident.id }))
      setDisplayId(targetIncident.displayId)
      trackEvent('Incident Drawer: Navigation', {
        direction,
        input_method: inputMethod,
      })
    },
    [dispatch, setDisplayId]
  )

  const navigateToPreviousIncident = useCallback(
    (inputMethod: InputMethod) =>
      navigateToIncident(previousIncident, 'previous', inputMethod),
    [navigateToIncident, previousIncident]
  )

  const navigateToNextIncident = useCallback(
    (inputMethod: InputMethod) =>
      navigateToIncident(nextIncident, 'next', inputMethod),
    [navigateToIncident, nextIncident]
  )

  useEffect(() => {
    if (!isOpen || !shouldShowNavigation) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return
      }

      const activeElement = document.activeElement as HTMLElement | null
      // If focus is in a text field, do not use ArrowUp/ArrowDown to change incidents.
      // Let those keys work normally for typing and moving the cursor.
      const isEditableElement =
        EDITABLE_TAGS.has(activeElement?.tagName?.toLowerCase() ?? '') ||
        activeElement?.isContentEditable

      if (isEditableElement) {
        return
      }

      if (event.key === 'ArrowUp' && canNavigatePrevious) {
        event.preventDefault()
        navigateToPreviousIncident('keyboard')
      }

      if (event.key === 'ArrowDown' && canNavigateNext) {
        event.preventDefault()
        navigateToNextIncident('keyboard')
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    isOpen,
    shouldShowNavigation,
    canNavigatePrevious,
    canNavigateNext,
    navigateToPreviousIncident,
    navigateToNextIncident,
  ])

  return {
    isFromIncidentsTable,
    showNavigation: shouldShowNavigation,
    canNavigatePrevious,
    canNavigateNext,
    navigateToPreviousIncident,
    navigateToNextIncident,
  }
}
